import React, {createContext, useContext, useEffect, useRef, useState} from "react"
// API
import {APIDelete, APIGet, APIGetBlob, APIPost, APIPostBlob, APIPostFile, APIPut} from "@api";
// Interfaces
import {PackagingApps, PackagingBrand, PackagingProduct, PackagingProductPaginated, PackagingRange} from "@/interfaces";
// Context
import {useUser} from "@/context/UserProvider";
import {useTranslation} from "react-i18next";
import {checkIfEmptyArray} from "@/utils";
import Loading from "@/components/Loading/Loading";
import {useErrorBoundary} from "@/context/ErrorBoundaryProvider";
import {initFiltersBackendModel} from "@/components/Products/filters/productFiltersManagement";

const {REACT_APP_API_URL} = process.env

const DataContext = createContext<any>(null)
export const useData = () => useContext(DataContext)

export default function PackagingDataProvider({children}: any) {

    const {token, organisation, user, markStepDone} = useUser()

    // Getting-started tracker: creation types that map to a tracked step.
    const CREATION_STEP: Record<string, string> = {brand: "create_brand", range: "create_range", product: "create_product"}
    const {handleError, handleSuccess, handleInfo} = useErrorBoundary()
    const {t} = useTranslation("common")

    let init = { brands: false, ranges: false, products: false }

    const [loading, setLoading] = useState<boolean>(true)
    // First visit checker
    const [firstVisit, setFirstVisit] = useState<any>(false)
    // data - watch for indentation, indicating in which section we are!
    const [brands, setBrands] = useState<any>(null)
        const [brandsLogos, setBrandsLogos] = useState<any>(null)
        const [rangesInBrands, setRangesInBrands] = useState<Record<string, number>>({})
        // This is actually RANGES count for BRANDS - should be its own item in <Brands>
    const [ranges, setRanges] = useState<any>(null)
        const [rangesLogos, setRangesLogos] = useState<any>(null)
    const [products, setProducts] = useState<any>(null)
        const [totalProducts, setTotalProducts] = useState<number>(0)
        const [batchesIds, setBatchesIds] = useState<any>(null)
    // TODO: refacto - this is a short-term solution
    const [downloadableProducts, setDownloadableProducts] = useState<any>(null)
        const [totalDownloadableProducts, setTotalDownloadableProducts] = useState<number>(0)
    // -----
    const [apps, setApps] = useState<any>(null)
    // Generator specific data sets
    const [domains, setDomains] = useState<any>(null)
    const [qrcodeTemplates, setQRCodesTemplates] = useState<any>(null)
    // Filters shared across panels
    const [commonFilters, setCommonFilters] = useState<any>({organisation: ""})
    // Filters propagation synthesis: every brand with a preset and every range
    // with an override of its own. null until loaded (or when the load failed),
    // so views can tell "nothing propagated" from "unknown".
    const [propagatedFilters, setPropagatedFilters] = useState<{brands: any[]; ranges: any[]} | null>(null)
    // The mount effect below re-runs as token then organisation arrive; the
    // state is still null while the first load is in flight, so without this
    // guard the synthesis was fetched three times on boot.
    const propagatedFiltersLoading = useRef<boolean>(false)
    const clearCommonFilters = () => {
        setCommonFilters({organisation: ""})
    }

    useEffect(() => {
        if (!!token && !!organisation) {
            if (!brands) getBrands().then()
            if (!ranges) getRanges().then()
            if (!products) getProducts().then()
            // Loaded up-front (not lazily on the Apps tab) so the sidebar can
            // show the apps count at first glance.
            if (!apps) getApps()
            // Shared by the Brands cards and the propagation page.
            if (!propagatedFilters) refreshPropagatedFilters()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, organisation])

    useEffect(() => {
        if (!!token && !!organisation && firstVisit !== undefined && brands !== null && ranges !== null && products !== null) {
            if (checkIfEmptyArray(brands)) { setFirstVisit("brands"); setLoading(false); init.brands = true; return; }
            else if (checkIfEmptyArray(ranges)) { setFirstVisit("ranges"); setLoading(false); init.ranges = true; return; }
            else if (checkIfEmptyArray(products)) { setFirstVisit("products"); setLoading(false); init.products = true; return; }
            else if (firstVisit === "products") setFirstVisit("end");
            else setFirstVisit(undefined); setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brands, ranges, products, organisation, token])

    // ANY
    const createAny = async (object: any, type: string) => {

        if ((!object && type !== "app") || !type) return

        let created

        let body
        let refresher: any
        let errorMessage: string

        switch(type) {
            case "brand":
                body = {
                    name: object.name,
                    description: object.description,
                    logo_asset_id: object.logo_id,
                    attributed_user_uuid: object.attributed_user_uuid || undefined,
                }
                refresher = () => getBrands()
                errorMessage = "error_create_brand"
                break;
            case "range":
                body = {
                    name: object.name,
                    description: object.description,
                    brand_id: object.brand,
                    logo_asset_id: object.logo_id,
                    attributed_user_uuid: object.attributed_user_uuid || undefined,
                }
                refresher = () => getRanges()
                errorMessage = "error_create_range"
                break;
            case "product":
                body = {
                    qr_code_id: object.qrcode,
                    range_id: object.range,
                    name: object.name,
                    sku: object.sku,
                    default_url: object.default_url,
                    filters: null,
                    bypass_app: object.bypass_app,
                    type: object?.type,
                    attributed_user_uuid: object.attributed_user_uuid || undefined,
                }
                refresher = () => getProducts()
                errorMessage = "error_create_product"
                break;
            case "app":
                body = {}
                refresher = () => getApps()
                errorMessage = "error_create_app"
                break;
            case "url":
                body = {
                    country_code: object.country.code,
                    destination_url: object.destination_url,
                    language_code: object.language.code,
                    product_id: object.product_id
                }
                refresher = () => getSingleProductUrls(object.product_id)
                errorMessage = "error_create_redirection_url"
                break;
            default:
                return;
        }

        await APIPost<any>(REACT_APP_API_URL + "/packaging/" + type + (type === "product" ? "?lfg=face" : ""), body, token)
            .then((data: any) => {
                if (data.ok) {
                    refresher()
                    created = true
                    handleSuccess(`${t(type)} ${t("created")}`)
                    if (CREATION_STEP[type]) markStepDone?.(CREATION_STEP[type])
                } else if (data.code === 403) {
                    handleError(t("error_duplicate_name"))
                } else {
                    handleError(t(errorMessage))
                }
            }).catch(() => handleError(t(errorMessage)));

        return created
    }

    const deleteAny = async (toDelete: any, type: any) => {

        if (!toDelete || !type) handleError(t("error_delete_request"))

        let deleted
        let endpoint
        let errorMessage: string
        let body

        switch (type) {
            case "filter":
                endpoint = "/" + type
                body = {
                    id: toDelete.id,
                    final_url: toDelete.destination_url
                }
                errorMessage = t("error_deleting") + " " + t("redirection_url")
                break;
            default:
                endpoint = "/packaging/" + type
                body = {
                    id: toDelete.id,
                    name: toDelete.name
                }
                errorMessage = t("error_deleting") + " " + t(type)
        }

        await APIDelete<any>(REACT_APP_API_URL + endpoint, body, token).then((data) => {
                if (data.ok) {
                    deleted = true
                    handleSuccess(`${t(type)} ${t("deleted")}` )
                } else {
                    handleError(errorMessage)
                }
            }).catch(() => handleError(errorMessage));

        return deleted
    }

    const editAny = async (object: any, type: string) => {

        if (!object || !type) return

        let updated

        let body
        let refresher: any
        let errorMessage: string

        // specific case for URLs call
        if (type.includes("url/")) {
            body = {
                id: type.replace("url/", ""),
                country_code: object.country.code,
                destination_url: object.destination_url,
                language_code: object.language.code,
                product_id: object.product_id
            }
            refresher = () => null
            errorMessage = "error_update_redirection_url"
        } else {
            switch(type) {
                case "brand":
                    body = {
                        id: object.id,
                        name: object.name,
                        description: object.description,
                        logo_asset_id: object.logo_id,
                        attributed_user_uuid: object.attributed_user_uuid || undefined,
                    }
                    refresher = () => getBrands()
                    errorMessage = "error_update_brand"
                    break;
                case "range":
                    body = {
                        id: object.id,
                        name: object.name,
                        description: object.description,
                        brand_id: object.brand,
                        logo_asset_id: object.logo_id,
                        attributed_user_uuid: object.attributed_user_uuid || undefined,
                    }
                    refresher = () => getRanges()
                    errorMessage = "error_update_range"
                    break;
                case "product":
                    body = {
                        id: object.id,
                        qr_code_id: object.qrcode,
                        range_id: object.range,
                        brand_id: object.brand,
                        name: object.name,
                        sku: object.sku,
                        default_url: object.default_url,
                        filters: object.filters,
                        bypass_app: object.bypass_app,
                        type: object?.type,
                        attributed_user_uuid: object.attributed_user_uuid || undefined,
                    }
                    refresher = () => getProducts()
                    errorMessage = "error_update_product"
                    break;
                case "app":
                    body = object
                    refresher = () => getApps()
                    errorMessage="error_update_app"
                    break;
                default:
                    return;
            }
        }

        await APIPut<any>(REACT_APP_API_URL + "/packaging/" + type + (type === "product" ? "?lfg=face" : ""), body, token)
            .then((data: any) => {
                if (data.ok) {
                    refresher()
                    updated = true
                    handleSuccess(`${t(type)} ${t("saved")}`)
                } else handleError(t(errorMessage))
            }).catch(() => {handleError(t(errorMessage))});

        return updated
    }

    // Brands
    const getBrands = async (organisationFilter?: any) => {

        if (!organisation) return

        const getRangesCount = (brandUuid: any) => {
            APIGet<any>(REACT_APP_API_URL + "/packaging/brand/" + brandUuid + "/ranges/count", token).then((data: any) => {
                if (!!data.parsedBody) {
                    setRangesInBrands(prev => ({ ...prev, [brandUuid]: data.parsedBody.count }));
                }
            }).catch(() => handleError(t("error_get_range_count")));
        }

        const getBrandsLogos = (brandsList: any) => {
            for (const brand of brandsList) {
                if (brand.logo_id) {
                    APIPostBlob<any>(REACT_APP_API_URL + "/asset/download/logo", {
                        asset_uuid: brand.logo_id,
                    }, token).then((data) => {
                        if (data.ok && data.rawBlob !== undefined) {
                            let img = window.URL.createObjectURL(data.rawBlob);
                            setBrandsLogos((prev: any) => ({ ...prev, [brand.id]: img }));
                        }
                    }).catch(() => {
                        handleError(t("error_get_logos"))
                    });
                }
            }
        }

        let orgUuid = !!organisationFilter ? `?org=${organisationFilter}` : ""

        await APIGet<Array<PackagingBrand>>(REACT_APP_API_URL +  `/packaging/brands${orgUuid}`, token)
            .then((data) => {
                if (!!data.parsedBody) {
                    getBrandsLogos(data.parsedBody)
                    setBrands(data.parsedBody)
                    for (const brand of data.parsedBody) {
                        getRangesCount(brand.id)
                    }
                } else setBrands([])
            }).catch(() => handleError(t("error_get_brands")))
    }

    // Ranges
    const getRanges = async (organisationFilter?: any) => {

        if (!organisation) return

        const getRangesLogos = (rangesList: any) => {
            for (const range of rangesList) {
                if (range.logo_id) {
                    APIPostBlob<any>(REACT_APP_API_URL + "/asset/download/logo", {
                        asset_uuid: range.logo_id,
                    }, token).then((data) => {
                        if (data.ok && !!data.rawBlob) {
                            let img = window.URL.createObjectURL(data.rawBlob);
                            setRangesLogos((prev: any) => ({ ...prev, [range.id]: img }));
                        }
                    }).catch(() => {
                        handleError(t("error_get_logos"))
                    })
                }
            }
        }

        let orgUuid = !!organisationFilter ? `?org=${organisationFilter}` : ""

        await APIGet<Array<PackagingRange>>(REACT_APP_API_URL + `/packaging/ranges${orgUuid}`, token)
            .then((data) => {
                if (!!data.parsedBody) {
                    getRangesLogos(data.parsedBody);
                    setRanges(data.parsedBody);
                } else setRanges([]);
            }).catch(() => {
            handleError(t("error_get_ranges"))
            setRanges([]);
        })
    }

    // Products
    const getProducts = async (filters?: any) => {

        if (!organisation) return

        let defaultParams = {
            range: "",
            brand: "",
            max_rows: "10",
            page: "1",
            search: "",
            org: "",
            type: "",
            default_url: ""
        }

        let params = new URLSearchParams(filters ? filters : {...defaultParams})

        await APIGet<PackagingProductPaginated>(REACT_APP_API_URL + "/packaging/products?" + params, token)
            .then((data) => {
                if (data.ok && !!data.parsedBody) {
                    setProducts(data.parsedBody.records);
                    setTotalProducts(data.parsedBody.total_records);
                } else {
                    setProducts([]);
                    setTotalProducts(0);
                }
            }).catch(() => {
            handleError(t("error_get_products"))
            setProducts([]);
            setTotalProducts(0);
        })
    }

    // Dashboard — total packaging scans over a rolling window (default 30 days).
    // The analytics search exposes no "type" filter, so we scope to packaging by
    // passing the packaging products' UUIDs (both Standard and GS1) as the
    // "QR Codes" filter — the exact mechanism the packaging→analytics deep link
    // already uses. Returns {count, comparison} or null on failure/empty.
    const getPackagingScans = async (days: number = 30, organisationFilter?: string) => {
        if (!organisation || !token) return null

        // 1) Collect packaging product UUIDs (capped to the analytics ~1700 limit).
        const params = new URLSearchParams({
            max_rows: "1700", page: "1",
            org: organisationFilter || "", range: "", brand: "", search: "", type: "", default_url: "",
        })

        let ids: string[] = []
        try {
            const res = await APIGet<PackagingProductPaginated>(REACT_APP_API_URL + "/packaging/products?" + params, token)
            if (res.ok && !!res.parsedBody) ids = res.parsedBody.records.map((p: any) => p.id)
        } catch {
            return null
        }

        if (!ids.length) return {count: 0, comparison: null}

        // 2) Ask the analytics service for visits over the last `days` days.
        const body = {
            filters: [{field: "QR Codes", value: ids, operator: "is_one_of", label: ["Packaging"]}],
            time_range: {type: 1, number: days, unit: "days"},
        }

        try {
            const res = await APIPost<any>(REACT_APP_API_URL + "/analytics/qrcodes/search", body, token)
            const pb: any = res.parsedBody
            if (res.ok && !!pb && typeof pb === "object" && typeof pb.visits_count === "number") {
                return {
                    count: pb.visits_count as number,
                    comparison: typeof pb.visits_count_comparison === "number" ? pb.visits_count_comparison as number : null,
                }
            }
            return null
        } catch {
            return null
        }
    }

    // Activity logs — packaging-scoped audit events for the current org.
    // Uses core's audit endpoint with the `source_app=packaging` filter, so only
    // packaging-related events come back. Returns {events, total} or null.
    const getPackagingActivityLogs = async (
        page: number = 1, perPage: number = 10, action?: string, orgUuid?: string,
    ) => {
        if (!token) return null
        const org = orgUuid || organisation?.organisation_id
        if (!org) return null

        const params = new URLSearchParams({
            source_app: "packaging",
            page: String(page),
            per_page: String(perPage),
        })
        if (action && action.trim()) params.set("action", action.trim())

        try {
            const res = await APIGet<any>(`${REACT_APP_API_URL}/audit/org/${org}?${params}`, token)
            const pb: any = res.parsedBody
            if (res.ok && !!pb && Array.isArray(pb.events)) {
                return {events: pb.events as any[], total: typeof pb.total === "number" ? pb.total : pb.events.length}
            }
            return null
        } catch {
            return null
        }
    }

    const getSingleProduct = async (productUuid: string) => {

        let response

        await APIGet<PackagingProduct>(REACT_APP_API_URL + "/packaging/product?uuid=" + productUuid, token)
            .then((data) => {
                if (data.ok && !!data.parsedBody) response = data.parsedBody
                else handleError("error_get_product")
            }).catch(() => handleError("error_get_product"));

        return response
    }

    const getSingleProductUrls = async (productUuid: string) => {

        let response

        await APIGet<any>(`${REACT_APP_API_URL}/packaging/product/${productUuid}/urls`, token)
            .then((data: any) => {
                if (data.ok && !!data.parsedBody && data.parsedBody) response = data.parsedBody})
            .catch(() => handleError(t("error_get_redirections")))

        return response
    }

    const archiveProducts = async (uuids: any) => {

        if (!uuids) return false

        let response: boolean = false
        let body = { uuids: uuids }

        await APIPut<any>(REACT_APP_API_URL + "/packaging/product/archive", body, token).then((res: any) => {
            if (!!res) response = true
            else handleError(t("error_archiving_products"))
        }).catch(() => handleError(t("error_archiving_products")))

        return response
    }

    // ─── Filters propagation (per-brand filter presets) ───
    // A preset is {country?, language?}; the backend generates one redirection
    // filter per entry when a product is created under the brand with no
    // filters of its own.

    const getBrandFilterPresets = async (brandUUID: string) => {
        if (!token || !brandUUID) return null

        // null = failed load. The page must not treat it as "no presets": the
        // empty state invites the user to add rows and save, which would
        // overwrite the presets that failed to load.
        try {
            const res = await APIGet<any>(`${REACT_APP_API_URL}/packaging/brand/${brandUUID}/filter_presets`, token)
            const pb: any = res.parsedBody
            if (res.ok && !!pb && Array.isArray(pb.presets)) return pb.presets as any[]
        } catch {}
        handleError(t("error_get_filter_presets"))
        return null
    }

    // Every brand that already has a preset and every range with its own
    // override. Brands with no preset and inheriting ranges are omitted by the
    // backend. `ranges` is optional in the payload (older backend): it reads
    // as "no override anywhere" rather than failing the whole load.
    const listFilterPresets = async () => {
        if (!token) return null

        try {
            const res = await APIGet<any>(`${REACT_APP_API_URL}/packaging/filter_presets`, token)
            const pb: any = res.parsedBody
            if (res.ok && !!pb && Array.isArray(pb.brands)) {
                return {brands: pb.brands as any[], ranges: Array.isArray(pb.ranges) ? (pb.ranges as any[]) : []}
            }
            return null
        } catch {
            return null
        }
    }

    // Reloads the synthesis; a failed reload keeps the last known state rather
    // than blanking the cards' hints. Silent: there is no toast for it.
    const refreshPropagatedFilters = async () => {
        if (propagatedFiltersLoading.current) return
        propagatedFiltersLoading.current = true
        try {
            const res = await listFilterPresets()
            if (res !== null) setPropagatedFilters(res)
        } finally {
            propagatedFiltersLoading.current = false
        }
    }

    const saveBrandFilterPresets = async (brandUUID: string, presets: any[]) => {
        // Token guard mirrors the two reads above: without it a save fired
        // during a token refresh went out as "Authorization: Bearer " → 401.
        if (!token || !brandUUID) return false

        let response: boolean = false
        const body = {presets: presets}

        await APIPut<any>(`${REACT_APP_API_URL}/packaging/brand/${brandUUID}/filter_presets`, body, token).then((res: any) => {
            if (!!res && res.ok) response = true
            else handleError(t("error_saving_filter_presets"))
        }).catch(() => handleError(t("error_saving_filter_presets")))

        if (response) refreshPropagatedFilters()
        return response
    }

    // A range's effective preset: `inherited: true` means the list is the
    // brand's (the range has no override). null = failed load, same contract
    // as getBrandFilterPresets.
    const getRangeFilterPresets = async (rangeUUID: string) => {
        if (!token || !rangeUUID) return null

        try {
            const res = await APIGet<any>(`${REACT_APP_API_URL}/packaging/range/${rangeUUID}/filter_presets`, token)
            const pb: any = res.parsedBody
            if (res.ok && !!pb && Array.isArray(pb.presets)) {
                return {presets: pb.presets as any[], inherited: !!pb.inherited, brandId: pb.brand_id as string}
            }
        } catch {}
        handleError(t("error_get_filter_presets"))
        return null
    }

    // Sets a range's own preset; [] clears the override so the range inherits
    // the brand's again. Resolves to the effective preset after the save (the
    // brand's when cleared) so the editor can show what now applies.
    const saveRangeFilterPresets = async (rangeUUID: string, presets: any[]) => {
        if (!token || !rangeUUID) return null

        try {
            const res = await APIPut<any>(`${REACT_APP_API_URL}/packaging/range/${rangeUUID}/filter_presets`, {presets}, token)
            const pb: any = res?.parsedBody
            if (!!res && res.ok) {
                refreshPropagatedFilters()
                return {
                    presets: Array.isArray(pb?.presets) ? (pb.presets as any[]) : presets,
                    inherited: !!pb?.inherited,
                }
            }
        } catch {}
        handleError(t("error_saving_filter_presets"))
        return null
    }

    // Writes one preset onto every brand the user can manage (own org and
    // sub-orgs). Resolves to how many brands changed / were already up to
    // date, null on failure. The backend rejects an empty list.
    const saveAllBrandsFilterPresets = async (presets: any[]) => {
        if (!token) return null

        try {
            const res = await APIPut<any>(`${REACT_APP_API_URL}/packaging/filter_presets`, {presets}, token)
            const pb: any = res?.parsedBody
            if (!!res && res.ok) {
                refreshPropagatedFilters()
                return {
                    updated: Array.isArray(pb?.updated) ? pb.updated.length : 0,
                    unchanged: typeof pb?.unchanged === "number" ? pb.unchanged : 0,
                }
            }
        } catch {}
        handleError(t("error_saving_filter_presets"))
        return null
    }

    const restoreProducts = async (uuids: any) => {

        let response: boolean = false
        let body = { uuids: uuids }

        await APIPut<any>(REACT_APP_API_URL + "/packaging/product/restore", body, token).then((res: any) => {
            if (!!res) response = true
            else handleError(t("error_restoring_products"))
        }).catch(() => handleError(t("error_restoring_products")))

        return response
    }

    // Apps management
    const getApps = (organisationFilter?: any) => {

        let organisationUuid = organisationFilter ? organisationFilter : organisation.organisation_id

        APIGet<PackagingApps[]>(REACT_APP_API_URL + "/packaging/apps?org=" + organisationUuid, token)
                .then((data) => {
                if (data.ok && !!data.parsedBody && data.parsedBody.length > 0) {
                    for (let app of data.parsedBody) {
                        if (app.brands === null) app.brands = [];
                        if (app.languages === null) app.languages = [];
                    }
                    setApps(data.parsedBody);
                } else setApps([]);
            }).catch(() => handleError(t("error_get_apps")))
    }

    // Deletes an app; its associated brands move to transferToAppId when
    // provided, otherwise they are detached (see DELETE /packaging/app).
    const deleteApp = async (appId: string, transferToAppId?: string) => {

        if (!appId) return false

        let deleted = false
        const errorMessage = t("error_deleting") + " " + t("app")

        await APIDelete<any>(REACT_APP_API_URL + "/packaging/app", {
            id: appId,
            transfer_to_app_id: transferToAppId ?? "",
        }, token).then((data) => {
            if (data.ok) {
                deleted = true
                handleSuccess(`${t("app")} ${t("deleted")}`)
            } else handleError(errorMessage)
        }).catch(() => handleError(errorMessage))

        return deleted
    }

    const getSingleApp = async (uuid: any) => {

        if (!uuid) return

        let response

        await APIGet<PackagingApps[]>(REACT_APP_API_URL + "/packaging/app/" + uuid, token)
            .then((data) => {
                if (data.ok && !!data.parsedBody) response = data.parsedBody
                else handleError(t("error_get_app"))
            }).catch(() => handleError(t("error_get_app")))

        return response
    }

    // Images
    const handleDropImage = async (dataDrop: any) => {

        let image
        let uuid
        let url

        await APIPostFile<any>(REACT_APP_API_URL + "/asset/upload/logo", dataDrop[0], token).then((data: any) => {
            if (data.ok)
                if (data.parsedBody !== undefined) {
                    image = URL.createObjectURL(dataDrop[0]);
                    uuid = data.parsedBody.asset_id
                    url = `https://unitag-assets.s3.eu-west-1.amazonaws.com/logo/${data.parsedBody.asset_id}`
                } else handleError(t("error_upload_image"))
        }).catch(() => handleError(t("error_upload_image")));

        dataDrop = null;

        return {image: image, uuid: uuid, url: url}
    }

    const getLogo = async (logoId: any) => {

        let logo: any = undefined

        await APIPostBlob<any>(REACT_APP_API_URL + "/asset/download/logo", {
            asset_uuid: logoId,
        }, token).then((data: any) => {
            if (data.status === 200) {
                if (data.rawBlob !== undefined) {
                    logo = window.URL.createObjectURL(data.rawBlob);
                }
            } else handleError(t("error_get_logo"))
        }).catch(() => handleError(t("error_get_logo")));

        return logo
    }

    // QR Codes
    const getSingleQRCode = async (qrcodeUuid: any) => {

        let response: {data: any, preview: any} = {
            data: null,
            preview: null
        }

        await APIGet<any>(REACT_APP_API_URL + "/qrcode/" + qrcodeUuid, token)
            .then((data) => {
                if (data.ok && data.parsedBody !== undefined) response.data = data.parsedBody
                else handleError(t("error_get_product_qrcode"))
            }).catch(() => handleError(t("error_get_product_qrcode")));

        await APIGetBlob<any>(REACT_APP_API_URL + "/qrcode/" + qrcodeUuid + "/preview", token)
            .then((data: any) => {
                if (data.ok && data.rawBlob !== undefined) response.preview = URL.createObjectURL(data.rawBlob)
                else handleError(t("error_get_product_qrcode_preview"))
            }).catch(() => handleError(t("error_get_product_qrcode_preview")));

        return response
    }

    const getSingleQRCodePreview = async (qrcodeUuid: string) => {

        let response

        await APIGetBlob<any>(REACT_APP_API_URL + "/qrcode/" + qrcodeUuid + "/preview", token)
            .then((data: any) => {
                if (data.ok && data.rawBlob !== undefined) response = URL.createObjectURL(data.rawBlob)
                // Removed the counterpart in order not to spam them like hell
            }).catch(() => handleError(t("error_get_product_qrcode_preview")));

        return response
    }

    const getWIPQRCodePreview = async (qrcode: any, type?: string) => {

        let response
        let querySuffix = type === "serial" ? "/qrcodes/serial/preview" : "/qrcodes/preview"

        await APIPostBlob<any>(REACT_APP_API_URL + querySuffix, qrcode, token)
            .then((data: any) => {
                if (data.ok && data.rawBlob !== undefined) {
                    let generatorHeader = data.headers.get("x-unitag-generator");
                    response = {preview: URL.createObjectURL(data.rawBlob), header: generatorHeader}
                } else {
                    response = undefined
                    if (data.status === 404) {
                        handleError(t("error_get_product_qrcode_preview"))
                    } else if (data.status === 400) {
                        handleError(t("short_url_already_in_use_GS1"))
                    } else handleError(t("error_get_product_qrcode_preview"))
                }
            }).catch(() => handleError(t("error_get_product_qrcode_preview")));

        return response
    }

    const handleDropQRCodeImage = async (type: "overlay" | "logo", input: any) => {
        // Used to combine both image-overlay and logo uploads
        // Use the `type` parameter ("overlay" || "logo") to trigger either of the behaviours

        let response

        await APIPostFile<any>(REACT_APP_API_URL + `/asset/upload/${type}`, input.target.files[0], token)
            .then((data) => {
                if (data.status === 200) response = data.parsedBody.asset_id
                else handleError(t("error_upload_image"))
            }).catch(() => handleError(t("error_upload_image")))

        return response
    }

    const getDomains = async () => {
        let result

        APIGet<any[]>(REACT_APP_API_URL + "/domains", token).then((data) => {
            if (data.status === 200 && !!data.parsedBody) {
                setDomains(data.parsedBody);
                result = data.parsedBody
            }
        }, () => {
            // removed the alert because the API sends a 401 for every other user
            // handleError(t("error_getting_domains"))
            result = []
        }).catch(() => result = []);

        return result
    }

    const getTemplates = async () => {
        APIGet<any[]>(REACT_APP_API_URL + "/qrcodes/templates", token).then((data) => {
            if (data.parsedBody !== undefined) {
                const templates = data.parsedBody;

                if (!!templates && templates.length > 0) {
                    let templatesList: any[] = [];
                    let promises = [];

                    for (let template of templates) {
                        promises.push(APIPostBlob<object>(REACT_APP_API_URL + "/qrcodes/preview", {
                            data: {
                                type: "URL",
                                resolution: "static",
                                url: "https://unitag.io",
                            },
                            settings: {
                                template_id: template.uuid,
                            }
                        }, token).then((data) => {
                            if (!!data.rawBlob) {
                                template.imgURLObject = URL.createObjectURL(data.rawBlob);
                                templatesList.push(template);
                            }
                        }));
                    }

                    Promise.all(promises).then(() => {
                        setQRCodesTemplates(templatesList);
                    });
                }
            }
        }, () => {
            handleError("error_getting_qrcode_templates")
        }).catch(() => handleError("error_getting_qrcode_templates"));
    }

    const quickDownloadSingleQRCode = async (uuid: any, label: any, gtin?: any) => {
        await APIPostBlob<any>(REACT_APP_API_URL + "/asset/download/qrcode_bundle", {
            asset_uuid: uuid,
            format: "png",
            size: 300,
            unit: 'px',
            hasFrame: false,
            frame: {
                text_bottom: "",
                position: "bottom",
                style: "borderContained"
            }
        }, token).then((data) => {
            if (data.ok) {
                markStepDone?.("download_product")
                if (data.rawBlob !== undefined) {
                    let url = window.URL.createObjectURL(data.rawBlob)
                    let a = document.createElement('a')
                    a.href = url
                    a.download = !!gtin ? label + "_" + gtin + ".png" : label + ".png"
                    a.click()
                }
            } else {
                data.rawBlob?.text().then((text: string) => {
                    if (text.includes("UPGRADE_NEEDED")) {
                        handleError(t("error_download_qrcode_upgrade"))
                    } else {
                        handleError(t("error_download_qrcode"))
                    }
                });
            }
        }).catch(() => handleError(t("error_download_qrcode")))
    }

    const downloadSingleQRCode = async (uuid: any, label: any, file: any, gtin?: any) => {

        let downloadPath: string = file.format === 'pdf' ? 'export_pdf' : 'qrcode_bundle';

        await APIPostBlob<any>(REACT_APP_API_URL + "/asset/download/" + downloadPath, {
            asset_uuid: uuid,
            format: file.format,
            size: Number(file.size),
            real_size: file.real_size,
            dim_x: Number(file.dim_x),
            unit: 'px',
            hasFrame: false,
            frame: {
                text_bottom: "",
                position: "bottom",
                style: "borderContained"
            },
            // What the PDF embeds: "vector" (GS1 / print quality) or "raster"
            // (default). Only meaningful for the export_pdf path.
            ...(file.format === 'pdf' && {pdf_content: file.pdf_content || 'raster'})
        }, token).then((data) => {
            if (data.ok) {
                if (data.rawBlob !== undefined) {
                    let url = window.URL.createObjectURL(data.rawBlob);
                    let a = document.createElement('a');
                    a.href = url;
                    a.download = !!gtin ? label + "_" + gtin + "." + file.format : label + "." + file.format

                    a.click();
                }
            } else {
                data.rawBlob?.text().then((text: string) => {
                    if (text.includes("UPGRADE_NEEDED")) {
                        handleError(t("error_download_qrcode_upgrade"));
                    } else {
                        handleError(t("error_download_qrcode"));
                    }
                });
            }
        }).catch(() => handleError(t("error_download_qrcode")))
    }

    // Products downloading
    const getDownloadableProducts = async (filters?: any) => {

        if (!organisation) return

        let defaultParams = {
            range: "",
            brand: "",
            max_rows: "9999",
            page: "1",
            search: "",
            org: "",
            type: ""
        }

        let params = new URLSearchParams(filters ? filters : {...defaultParams})

        let resultUuids: string[] = []
        await APIGet<PackagingProductPaginated>(REACT_APP_API_URL + "/packaging/products?" + params, token)
            .then((data) => {
                if (data.ok && !!data.parsedBody && !!data.parsedBody.records && data.parsedBody.records.length > 0) {
                    let uuids = []
                    for (const record of data.parsedBody.records) {
                        uuids.push(record.qr_code.id)
                    }
                    resultUuids = uuids
                    setDownloadableProducts(uuids);
                    setTotalDownloadableProducts(uuids.length);
                } else {
                    setDownloadableProducts([]);
                    setTotalDownloadableProducts(0);
                }
            }).catch(() => {
                handleError(t("error_get_products"))
                setDownloadableProducts([]);
                setTotalDownloadableProducts(0);
            })
        return resultUuids
    }


    const downloadQRCodesBatch = async (uuids: any, file: any, type?: string) => {

        let downloadPath: string = file.format === 'pdf' ? 'export_pdf' : 'export';

        const d = new Date()
        const todayFormated = `${d.getFullYear()}_${d.getMonth()}_${d.getDay()}_${d.getHours()}-${d.getMinutes()}-${d.getSeconds()}`
        let assetName: string = 'Unitag-Packaging-Export_' + todayFormated;
        assetName += file.format === 'pdf' ? '.pdf' : '.zip';

        let body: any = {
            format: file.format,
            size: Number(file.size),
            real_size: file.real_size,
            dim_x: Number(file.dim_x),
            color_profile: "RGB",
            unit: 'px',
            hasFrame: false,
            frame: {
                text_bottom: "",
                position: "bottom",
                style: "borderContained"
            }
        }

        // What the PDF embeds per QR: "vector" (GS1 / print quality) or
        // "raster" (default). Only meaningful for the export_pdf path.
        if (file.format === 'pdf') body = {...body, pdf_content: file.pdf_content || 'raster'}

        if (!type || type === "files") body = {...body, qrcode_ids: uuids }
        else if (type === "batch") body = {...body, batch_id: uuids}

        await APIPostBlob<any>(REACT_APP_API_URL + "/asset/download/" + downloadPath, body, token)
            .then((data) => {
            if (data.ok) {
                markStepDone?.("download_product")
                if (data.rawBlob !== undefined) {
                    let url = window.URL.createObjectURL(data.rawBlob);
                    let a = document.createElement('a');
                    a.href = url;
                    a.download = assetName;
                    a.click();
                }
            } else {
                data.rawBlob?.text().then(() => {
                    handleError(t("error_download_qrcode"))
                });
            }
        }).catch(() => handleError(t("error_download_qrcode")))
    }


    // GS1
    const createQRCode = async (qrcode: any, type?: string) => {

        let response
        let querySuffix = type === "serial" ? "/qrcode/serial" : "/qrcode"
        if (!qrcode) return false

        await APIPost<any>(REACT_APP_API_URL + querySuffix, qrcode, token).then((res: any) => {
            if (!!res) response = res.parsedBody
            else handleError(t("error_create_qrcode"))
        }).catch(() => handleError(t("error_create_qrcode")))

        return response
    }

    const getGS1QRCode = async (uuid: any) => {
        let response: { data: any, preview: any} = {
            data: null,
            preview: null
        }

        if (!uuid) return false
        // logic on GET /qrcode/serial
        await APIGet<any>(REACT_APP_API_URL + "/qrcode/" + uuid, token).then((res) => {
            if (!!res) response.data = res.parsedBody
            else handleError("error_get_qrcode")
        }).catch(() => handleError("error_get_qrcode"))

        await APIGetBlob<any>(REACT_APP_API_URL + "/qrcode/" + uuid + "/serial/preview", token).then((res: any) => {
            if (res.ok && res.rawBlob !== undefined) response.preview = URL.createObjectURL(res.rawBlob)
            else handleError(t("error_get_product_qrcode_preview"))
        }).catch(() => handleError(t("error_get_product_qrcode_preview")))

        return response
    }

    const getGS1QRCodePreview = async (uuid: any) => {
        let response
        if (!uuid) return false
        // logic on GET /qrcode/serial
        await APIGetBlob<any>(REACT_APP_API_URL + "/qrcode/" + uuid + "/serial/preview", token).then((res: any) => {
            if (res.ok && res.rawBlob !== undefined) response = URL.createObjectURL(res.rawBlob)
        }).catch(() =>handleError(t("error_get_product_qrcode_preview")))

        return response
    }

    const verifyGTIN = async (gs1Object: any) => {

        let response: any = null

        if (!gs1Object) return false

        const returnKeyIdentifiers = (ki: any) => {

            let keyIdentifiers: any[] = []

            ki.forEach((item: any) => {
                if (!!item.key && !!item.value) keyIdentifiers.push(item)
            })

            return keyIdentifiers
        }

        let body = {
            gtins: [
                {
                    primary_key: gs1Object.primaryKey,
                    value: gs1Object.primaryKeyValue.replaceAll("/", "%2F"),
                    consumer_product_variant: gs1Object?.consumer_product_variant,
                    batch_number: gs1Object?.batch_number,
                    serial_number: gs1Object?.serial_number,
                    data_attributes: returnKeyIdentifiers(gs1Object?.dataAttributes),
                    key_identifiers: returnKeyIdentifiers(gs1Object?.key_identifiers)
                }
            ]
        }

        await APIPost<any>(REACT_APP_API_URL + "/gs1/look-up", body, token).then((data) => {
            if (data.status === 500) {
                if (data.parsedBody === "invalid gtin value, last digit is incorrect") handleError(t("error_gtin_check_digit"))
                else handleError(t("error_verifying_gtin"))
                return null
            }

            if (!!data.parsedBody) {
                response = data.parsedBody
                handleSuccess(t("success_verifying_gtin"))
            } else handleError(t("error_verifying_gtin"))
        }).catch(() => handleError(t("error_verifying_gtin")))

        return response
    }

    // Misc
    const downloadSpreadsheet = async (filters: any) => {

        let body = {
            "filter_brand_uuids": filters.brands,
            "filter_range_uuids": filters.ranges
        }

        await APIPostBlob<any>(REACT_APP_API_URL + "/packaging/export", body, token).then((data) => {
            if (data.rawBlob !== undefined) {
                // Getting-started: the export half of the update-by-batch flow.
                markStepDone?.("update_products_batch")
                let url = window.URL.createObjectURL(data.rawBlob)
                let a = document.createElement('a')
                a.href = url
                a.download = 'Unitag Packaging export.xlsx'
                a.click()
            } else handleError(t("error_download_spreadsheet"))
        }).catch(() => handleError(t("error_download_spreadsheet")))
    }

    // Same spreadsheet as downloadSpreadsheet, scoped to a product selection
    // (the Products table's "export selected" action).
    const downloadProductsSpreadsheet = async (productUuids: string[]) => {

        if (!productUuids || !productUuids.length) return

        await APIPostBlob<any>(REACT_APP_API_URL + "/packaging/export/product", {"product_uuids": productUuids}, token).then((data) => {
            if (data.rawBlob !== undefined) {
                let url = window.URL.createObjectURL(data.rawBlob)
                let a = document.createElement('a')
                a.href = url
                a.download = 'Unitag Packaging export.xlsx'
                a.click()
            } else handleError(t("error_download_spreadsheet"))
        }).catch(() => handleError(t("error_download_spreadsheet")))
    }


    // Healthchecks V2
    const toggleHealthchecks = (status: string, settings?: any) => {

        let body: any = {
            // enabled: false,
            interval: 1440,
            retries: 1,
            retryInterval: 1,
            notifyAddresses: [
                user.email
            ]
        }

        if (!!settings) body = settings

        switch (status) {
            case "setup":
                APIPost(process.env.REACT_APP_HEALTHCHECKS_SERVICE + "/healthchecks/", body, token)
                    .then(() => markStepDone?.("launch_healthchecks"))
                    .catch(() => handleError(t("error_creating_healthchecks")))
                break
            case "disable":
                body = {...body, enabled: false}
                APIPut(process.env.REACT_APP_HEALTHCHECKS_SERVICE + "/healthchecks/", body, token)
                    .catch(() => handleError(t("error_updating_healthchecks")))
                break
            case "enable":
                body = {...body, enabled: true}
                APIPut(process.env.REACT_APP_HEALTHCHECKS_SERVICE + "/healthchecks/", body, token)
                    .then(() => markStepDone?.("launch_healthchecks"))
                    .catch(() => handleError(t("error_updating_healthchecks")))
                return
            case "kill":
                // TODO: weird - it catches an error while there's just a 204 "no content" response
                APIDelete(process.env.REACT_APP_HEALTHCHECKS_SERVICE + "/healthchecks/", {}, token)
                    .catch(() => handleError(t("error_deleting_healthcheck")))
                return
            default: return
        }
    }

    const updateHealthchecks = (parameters: any) => {
        APIPut(process.env.REACT_APP_HEALTHCHECKS_SERVICE + "/healthchecks/", parameters, token)
            .then()
            .catch((err: any) => handleError(err))
    }

    const getHealthchecksStatus = async () => {

        let response = null
        // Removed error catching to prevent error spam when no HC are created
        await APIGet(process.env.REACT_APP_HEALTHCHECKS_SERVICE + "/healthchecks/", token).then((res: any) => {
            if (!!res) response = res.parsedBody
            else handleError(t("error_get_heathchecks"))
        }).catch(() => response = "no healthchecks")

        return response
    }

    // Batches IDs
    const getBatchesIDs = async () => {
        await APIGet(process.env.REACT_APP_API_URL + "/packaging/batches", token).then((res: any) => {
            if (!!res.parsedBody.count) setBatchesIds(res.parsedBody.packaging_batch)
            else handleInfo(t("no_batches_found"))

        }).catch((err: any) => { handleError(err) })
    }

    // Product filters
    const initBackendFilters = async () => {
        await APIPut(process.env.REACT_APP_API_URL + "/user/qrfilters?filterType=packaging", initFiltersBackendModel, token)
            .then(() => {})
            .catch((e) => handleError(e))
    }

    if (loading && (!init.brands || !init.ranges || !init.products)) return <Loading type={"packaging-data"} />

    return (
        <DataContext.Provider
            value={{
                createAny,
                editAny,
                deleteAny,
                // Brands
                brands, brandsLogos, rangesInBrands,
                getBrands,
                // Ranges
                ranges, rangesLogos, getRanges,
                // Products
                products, totalProducts,
                getProducts, getSingleProduct, getSingleProductUrls,
                getPackagingScans, getPackagingActivityLogs,
                archiveProducts, restoreProducts,
                // Filters propagation
                propagatedFilters, refreshPropagatedFilters,
                getBrandFilterPresets, saveBrandFilterPresets,
                getRangeFilterPresets, saveRangeFilterPresets, saveAllBrandsFilterPresets,
                // Apps
                apps,
                getApps, getSingleApp, deleteApp,
                // Images
                getLogo,
                handleDropImage,
                // QR CODES
                getSingleQRCode, getSingleQRCodePreview,
                getWIPQRCodePreview,
                handleDropQRCodeImage,
                getDomains, domains,
                getTemplates, qrcodeTemplates,
                downloadSingleQRCode, quickDownloadSingleQRCode,
                // GS1
                verifyGTIN,
                createQRCode, getGS1QRCode, getGS1QRCodePreview,
                // Misc
                downloadSpreadsheet,
                downloadProductsSpreadsheet,
                firstVisit, setFirstVisit,
                // Healthchecks
                toggleHealthchecks, updateHealthchecks, getHealthchecksStatus,
                // Filters
                commonFilters, setCommonFilters, clearCommonFilters,

                // Products download
                getDownloadableProducts,
                downloadableProducts, totalDownloadableProducts,
                downloadQRCodesBatch,
                // batches IDs
                getBatchesIDs, batchesIds,

                initBackendFilters
            }}
        >
            {children}
        </DataContext.Provider>
    )
}